import React, { useMemo, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useNavigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { createAppTheme } from './theme';
import { NetworkProvider } from './contexts/NetworkContext';
import { useThemeMode, ThemeModeContext } from './hooks/useThemeMode';
import Login from './components/Login';
import RegisterPage from './components/RegisterPage';
import Dashboard from './components/Dashboard';
import PlanExpiryGate from './components/PlanExpiryGate';
import POS from './components/POS';
import CaptainApp from './components/CaptainApp';
import CustomerOrderPage from './components/CustomerOrderPage';
import LoadingScreen from './components/common/LoadingScreen';

// Import fonts
import '@fontsource/inter/300.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import '@fontsource/inter/800.css';
import '@fontsource/space-grotesk/400.css';
import '@fontsource/space-grotesk/500.css';
import '@fontsource/space-grotesk/600.css';
import '@fontsource/space-grotesk/700.css';

// Protected route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  return <NetworkProvider>{children}</NetworkProvider>;
};

// Role-based route component
const RoleRoute: React.FC<{
  children: React.ReactNode,
  allowedRoles: string[]
}> = ({ children, allowedRoles }) => {
  const { user } = useAuth();

  if (!user || !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" />;
  }

  return <>{children}</>;
};

// Plan-based feature route component
const PlanRoute: React.FC<{
  children: React.ReactNode,
  featureKey: 'HasPOS' | 'HasInventory' | 'HasCRM' | 'HasOnlineOrdering' | 'HasDelivery' | 'HasKDS' | 'HasReports'
}> = ({ children, featureKey }) => {
  const { user } = useAuth();

  if (user?.role === 'SuperAdmin') {
    return <>{children}</>;
  }

  const cached = localStorage.getItem('cached_plan_features');
  if (cached) {
    try {
      const planFeatures = JSON.parse(cached);
      if (planFeatures[featureKey] !== true) {
        return <Navigate to="/dashboard" />;
      }
    } catch {}
  }

  return <>{children}</>;
};

// QR Route Handler Component
const QRRouteHandler: React.FC = () => {
  const { restaurantCode, tableCode } = useParams<{ restaurantCode: string; tableCode: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    if (restaurantCode && tableCode) {
      // Store table context in localStorage
      localStorage.setItem('qrTableContext', JSON.stringify({
        restaurantCode,
        tableCode,
        timestamp: new Date().toISOString()
      }));
      
      // Redirect to POS page
      navigate('/pos');
    } else {
      // Invalid QR URL, redirect to login
      navigate('/login');
    }
  }, [restaurantCode, tableCode, navigate]);

  return <LoadingScreen />;
};

const IMAGE_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5134/api').replace('/api', '');

function AppContent() {
  const { user, currentRestaurant, currentTenant } = useAuth();

  useEffect(() => {
    // 1. Update Title
    if (currentRestaurant?.name) {
      document.title = `${currentRestaurant.name} - MenuMaster`;
    } else if (currentTenant?.name) {
      document.title = `${currentTenant.name} - MenuMaster`;
    } else if (user?.role === 'SuperAdmin') {
      document.title = 'Super Admin Dashboard - MenuMaster';
    } else if (user) {
      document.title = 'Dashboard - MenuMaster';
    } else {
      document.title = 'MenuMaster POS';
    }

    // 2. Update Favicon
    const updateFavicon = (href: string) => {
      let link: HTMLLinkElement | null = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.getElementsByTagName('head')[0].appendChild(link);
      }
      link.href = href;
    };

    if (currentRestaurant?.logoUrl) {
      const logoUrl = currentRestaurant.logoUrl.startsWith('http') 
        ? currentRestaurant.logoUrl 
        : `${IMAGE_BASE}${currentRestaurant.logoUrl}`;
      updateFavicon(logoUrl);
    } else if (currentRestaurant) {
      // Fallback: food/cooking emoji SVG data URI if restaurant exists but logoUrl is empty
      updateFavicon(`data:image/svg+xml,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 100 100%22><text y=%22.9em%22 font-size=%2290%22>🍳</text></svg>`);
    } else {
      // Default: MenuMaster logo or fallback
      updateFavicon('/favicon.ico');
    }
  }, [currentRestaurant, currentTenant, user]);

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <PlanExpiryGate>
              <Dashboard />
            </PlanExpiryGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/pos"
        element={
          <ProtectedRoute>
            <RoleRoute allowedRoles={['Staff', 'Admin', 'SuperAdmin']}>
              <PlanRoute featureKey="HasPOS">
                <POS />
              </PlanRoute>
            </RoleRoute>
          </ProtectedRoute>
        }
      />
      {/* Captain App — mobile waiter ordering interface */}
      <Route
        path="/captain"
        element={
          <ProtectedRoute>
            <RoleRoute allowedRoles={['Staff', 'Admin', 'SuperAdmin']}>
              <PlanRoute featureKey="HasPOS">
                <CaptainApp />
              </PlanRoute>
            </RoleRoute>
          </ProtectedRoute>
        }
      />
      {/* Public QR self-ordering — no auth required */}
      <Route path="/order/:tableId" element={<CustomerOrderPage />} />
      {/* QR Code Route: /r/{restaurantCode}/t/{tableCode} */}
      <Route path="/r/:restaurantCode/t/:tableCode" element={<QRRouteHandler />} />
      <Route
        path="/pricing-plans"
        element={
          <ProtectedRoute>
            <RoleRoute allowedRoles={['SuperAdmin']}>
              <Navigate to="/dashboard?section=pricing" />
            </RoleRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/"
        element={
          <Navigate to={user ? "/dashboard" : "/login"} />
        }
      />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  const themeMode = useThemeMode();
  const theme = useMemo(() => createAppTheme(themeMode.mode), [themeMode.mode]);

  return (
    <ThemeModeContext.Provider value={themeMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LanguageProvider>
          <Router>
            <AuthProvider>
              <AppContent />
            </AuthProvider>
          </Router>
        </LanguageProvider>
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}

export default App;
